// 1027 D1：首页视觉改版 —— 方向 A「专业策略顾问型」（暖白底 / 深海军蓝 / 品牌橙点缀）。
// 视觉依据 Docs/108_mockup/index.html（Kevin 已确认）；内容与文案沿用 1026 线上版。
// 新增：首屏营销问题输入框（提交后先引导注册）、D4 真实生成成果区、D3 定价周期切换。
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { BrandLogo } from "../components/BrandLogo";
import "./Landing.css";

const outputSlides = [
  { image: "/assets/landing/deck-signal.svg", cn: "消费者判断", en: "CONSUMER SIGNAL", index: "01" },
  { image: "/assets/landing/deck-direction.svg", cn: "品牌方向", en: "BRAND DIRECTION", index: "04" },
  { image: "/assets/landing/deck-growth.svg", cn: "增长计划", en: "GROWTH PLAN", index: "07" },
];

const painQuestions = [
  "新品上市，不知道如何打开市场？",
  "促销活动，投入不少却难以形成增长？",
  "产品推广，卖点很多却无法打动用户？",
  "品牌事件，如何找到真正有效的传播切入点？",
];

const teamMembers = [
  "国际 4A 广告集团资深创意总监",
  "深耕行业 20 年+ 的资深导演",
  "社交零售电商高管",
  "科技公司技术大牛",
];

const workflow = [
  { code: "K", name: "市场洞察", scope: "看清问题 — 市场 / 消费者 / 竞争", en: "Keen Insight", topics: ["品牌事实", "市场环境", "消费者", "竞争格局", "行业信号"] },
  { code: "B", name: "品牌策略构建", scope: "做出选择 — 定位 / 价值 / 主张", en: "Brand Blueprint", topics: ["品牌机会", "定位", "核心价值", "北极星语句"] },
  { code: "S", name: "营销行动落地", scope: "变成行动 — 创意 / 传播 / 行动", en: "Strategy Solution", topics: ["营销策略", "创意概念", "传播策略", "内容策略", "执行路径"] },
];

const strategyValues = [
  { title: "KBS策略引擎", detail: "把20年品牌营销策略经验结构化" },
  { title: "多Agent协同", detail: "研究、洞察、品牌、营销、传播等角色协同工作" },
  { title: "策略验证机制", detail: "事实、证据、判断、假设分层验证" },
  { title: "可编辑策略交付", detail: "输出PowerPoint可编辑文件" },
];

const capabilityLayers = [
  {
    lv: "第一层", title: "Strategy Intelligence", label: "策略研究与判断",
    items: [
      ["01", "调研与引用", "行业、竞品、消费者都有来源。"],
      ["02", "消费者与市场洞察", "从资料与研究中提炼可验证洞察。"],
      ["03", "策略与核心概念", "从洞察到品牌选择，再到营销策略。"],
    ],
  },
  {
    lv: "第二层", title: "Strategy Delivery", label: "策略交付与编辑",
    items: [
      ["04", "经营测算", "预算、渠道与目标进入同一套增长模型。"],
      ["05", "在线编辑", "修改文字、图片、页序并保留版本。"],
      ["06", "PPTX导出", "PowerPoint / WPS 继续编辑。"],
    ],
  },
];

// D3 / 105-P1：个人版订阅周期定价（半年 9 折、年付 8 折）
const personalCycles = {
  m: { price: "¥398", unit: " / 人 / 月", desc: "适合有持续策略需求的营销从业者与独立策划人员。" },
  h: { price: "¥2,149", unit: " / 人 / 半年", desc: "半年订阅 9 折（原价 ¥2,388），适合项目制密集期的营销团队骨干。" },
  y: { price: "¥3,820", unit: " / 人 / 年", desc: "按年订阅 8 折（原价 ¥4,776），适合全年持续产出的资深营销人。" },
} as const;

type CycleKey = keyof typeof personalCycles;

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const ctaTo = user ? "/new" : "/login";
  const [question, setQuestion] = useState("");
  const [cycle, setCycle] = useState<CycleKey>("m");
  const personal = personalCycles[cycle];

  // 决策（1027）：首屏输入框提交后先引导注册/登录，问题随 query 带过去
  const onAsk = (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    navigate(`${ctaTo}?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="aim-landing">
      <a className="aim-skip" href="#aim-main">跳到主要内容</a>
      <header className="aim-header">
        <div className="aim-header-inner">
          <Link className="aim-wordmark" to="/" aria-label="策略咖首页">
            <BrandLogo />
          </Link>
          <nav className="aim-nav" aria-label="主导航">
            <a href="#output">生成成果</a>
            <a href="#workflow">KBS方法</a>
            <a href="#capabilities">策略能力</a>
            <a href="#security">数据安全</a>
            <a href="#pricing">方案定价</a>
          </nav>
          <div className="aim-header-actions">
            <Link className="aim-text-link" to="/projects">我的项目</Link>
            <Link className="aim-button aim-button-primary" to={ctaTo}>开始写策略 <span aria-hidden="true">↗</span></Link>
          </div>
        </div>
      </header>

      <main id="aim-main">
        {/* 首屏：标题 + 营销问题输入框 + 痛点四问 + Slogan */}
        <section className="aim-hero" id="top">
          <div className="aim-hero-inner">
            <h1 className="aim-hero-statement">一个很懂品牌营销的<br /><em>AI 策略伙伴</em></h1>
            <p className="aim-hero-description">从一个真实的营销问题开始，帮你看清市场、找到机会、做出品牌选择，并形成可执行的营销策略。</p>
            <form className="aim-ask" onSubmit={onAsk}>
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="直接描述你的营销问题，例如：为一款新品制定上市营销策略"
                aria-label="输入你的营销问题"
              />
              <button className="aim-button aim-button-primary" type="submit">开始 <span aria-hidden="true">↗</span></button>
            </form>
            <p className="aim-ask-hint"><b>提交后先注册/登录</b>，即可让策略咖为你的问题生成完整方案 —— 从浏览到使用，一步直达。</p>
            <ul className="aim-hero-questions">
              {painQuestions.map((q) => <li key={q}>{q}</li>)}
            </ul>
            <p className="aim-hero-slogan">策略咖，让每一个营销难题，都有专业解决方案。</p>
          </div>
        </section>

        {/* 能力条 */}
        <section className="aim-proof-strip" aria-label="策略咖的四项核心能力">
          <div className="aim-proof-inner">
            {strategyValues.map((item) => <div key={item.title}><strong>{item.title}</strong><span>{item.detail}</span></div>)}
          </div>
        </section>

        {/* 团队背书 */}
        <section className="aim-section aim-team" id="team">
          <div className="aim-wrap">
            <div className="aim-section-heading">
              <p className="aim-eyebrow">The Team / 团队背书</p>
              <h2>20年一线实战，<br /><em>把经验变成 AI 的策略能力。</em></h2>
            </div>
            <div className="aim-team-body">
              <div>
                <p className="aim-team-lead">策略咖由拥有 <strong>20 年以上一线品牌营销实战经验</strong>的 <strong>Captain K 团队</strong>发起，将真实商业项目中积累的营销经验、策略思维与判断标准，转化为可复用、可迭代的 AI 策略能力。</p>
                <div className="aim-team-captain">
                  <strong>Captain K</strong>
                  <p>曾在全球最大广告集团及中国最大营销集团担任品牌营销要职，职业生涯横跨汽车、科技、消费品等多行业。</p>
                </div>
              </div>
              <ul className="aim-team-members">
                {teamMembers.map((m) => <li key={m}>{m}</li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* D4 真实生成成果（素材：测试项目生成） */}
        <section className="aim-section aim-output" id="output">
          <div className="aim-wrap">
            <div className="aim-section-heading">
              <p className="aim-eyebrow">01 / The Output</p>
              <h2>不是一堆答案。<br />是一套能上会的<em>营销提案。</em></h2>
              <p>每一页都有观点、结构和视觉重心。方案不是模板填空，而是一条完整的说服链路。</p>
              <span className="aim-showcase-note">以下页面为测试项目真实生成成果（演示数据，后续将替换为实际产出截图）</span>
            </div>
            <div className="aim-deck-grid">
              {outputSlides.map((item) => (
                <figure className="aim-deck-card" key={item.image}>
                  <div className="aim-deck-page"><img src={item.image} alt={`${item.cn}（${item.en}）提案页`} /></div>
                  <figcaption><b>{item.index} {item.cn}</b> · 测试项目「母婴洗护新品上市」</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 创始团队寄语 */}
        <section className="aim-message" id="message">
          <div className="aim-message-inner">
            <p className="aim-eyebrow">创始团队寄语</p>
            <blockquote>
              <p>我们提供的不是简单的营销想法，而是对品牌和产品营销的超级智慧。</p>
              <p>策略咖的目标，不是让 AI 替你堆砌更多方案，而是帮助你更快看清问题、建立正确的策略逻辑，并把专业判断转化为可执行的营销行动。</p>
            </blockquote>
          </div>
        </section>

        {/* KBS 三层策略引擎 */}
        <section className="aim-section aim-workflow" id="workflow">
          <div className="aim-wrap">
            <div className="aim-section-heading">
              <p className="aim-eyebrow">02 / Strategy Engine</p>
              <h2>把复杂的营销工作，<br /><em>规划成一套清晰的策略流程。</em></h2>
            </div>
            <div className="aim-kbs-grid">
              {workflow.map((item) => (
                <div className="aim-kbs-card" key={item.code}>
                  <span className="aim-kbs-code">{item.code}</span>
                  <h3>{item.name}</h3>
                  <p className="aim-kbs-en">{item.en}</p>
                  <p className="aim-kbs-scope">{item.scope}</p>
                  <ul>{item.topics.map((topic) => <li key={topic}>{topic}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 策略能力 */}
        <section className="aim-section aim-capabilities" id="capabilities">
          <div className="aim-wrap">
            <div className="aim-section-heading">
              <p className="aim-eyebrow">03 / Connected Capabilities</p>
              <h2>从洞察到交付，<br /><em>每一环都连在一起。</em></h2>
            </div>
            <div className="aim-capability-layers">
              {capabilityLayers.map((layer) => (
                <section className="aim-capability-layer" key={layer.title}>
                  <div className="aim-capability-layer-heading">
                    <p className="aim-capability-lv">{layer.lv}</p>
                    <h3>{layer.title}</h3>
                    <span>{layer.label}</span>
                  </div>
                  <div className="aim-capability-list">
                    {layer.items.map(([index, title, detail]) => (
                      <article key={index}><span>{index}</span><h4>{title}</h4><p>{detail}</p></article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </section>

        {/* 数据安全 */}
        <section className="aim-security" id="security">
          <div className="aim-security-inner">
            <div className="aim-security-mark">PRIVATE<br />BY DESIGN</div>
            <div className="aim-security-copy">
              <p className="aim-eyebrow">04 / Data Security</p>
              <h2>你的资料，只用来完成<em>你的方案。</em></h2>
              <p>项目按账号严格隔离，全程加密传输；方案可随时导出或删除。企业客户可配置专属模型与私有化部署。</p>
              <Link className="aim-underlined-link" to="/security">查看数据安全说明 <span aria-hidden="true">↗</span></Link>
            </div>
          </div>
        </section>

        {/* 注册与付费（D3：周期切换 + 企业版 3 人共用） */}
        <section className="aim-section aim-pricing" id="pricing">
          <div className="aim-wrap">
            <div className="aim-section-heading">
              <p className="aim-eyebrow">Start Now / 注册与付费</p>
              <h2>现在开始，<br />构建<em>专属策略。</em></h2>
              <p>无论你是独立营销人、广告策划师，还是企业品牌与市场负责人，都可以从实际营销问题出发，体验策略咖的策略撰写能力。</p>
            </div>
            <div className="aim-cycle-toggle" role="tablist" aria-label="订阅周期">
              <button className={cycle === "m" ? "is-active" : ""} role="tab" aria-selected={cycle === "m"} onClick={() => setCycle("m")}>按月订阅</button>
              <button className={cycle === "h" ? "is-active" : ""} role="tab" aria-selected={cycle === "h"} onClick={() => setCycle("h")}>半年订阅<span className="aim-save">省 9 折</span></button>
              <button className={cycle === "y" ? "is-active" : ""} role="tab" aria-selected={cycle === "y"} onClick={() => setCycle("y")}>按年订阅<span className="aim-save">省 8 折</span></button>
            </div>
            <div className="aim-pricing-groups">
              <div className="aim-pricing-group">
                <h3>个人版<span>为每一次营销任务提供策略支持</span></h3>
                <div className="aim-pricing-cards">
                  <article className="aim-price-card">
                    <p className="aim-price-name">尝鲜版</p>
                    <p className="aim-price-value">¥9.8<small> / 次</small></p>
                    <p className="aim-price-desc">适合首次体验策略咖，针对一个具体营销问题生成专业策略方案。</p>
                    <Link className="aim-button aim-button-dark" to={ctaTo}>立即尝鲜 <span aria-hidden="true">↗</span></Link>
                  </article>
                  <article className="aim-price-card">
                    <p className="aim-price-name">订阅版</p>
                    <p className="aim-price-value">{personal.price}<small>{personal.unit}</small></p>
                    <p className="aim-price-desc">{personal.desc}</p>
                    <Link className="aim-button aim-button-dark" to={ctaTo}>开通个人版 <span aria-hidden="true">↗</span></Link>
                  </article>
                </div>
              </div>
              <div className="aim-pricing-group">
                <h3>企业版<span>为品牌与营销团队建立专属策略能力</span></h3>
                <div className="aim-pricing-cards aim-pricing-cards-single">
                  <article className="aim-price-card aim-price-card-dark">
                    <p className="aim-price-name">企业版 · 3 人共用</p>
                    <p className="aim-price-value">¥9,800<small> / 企业 / 月</small></p>
                    <p className="aim-price-desc">适合需要持续开展品牌战略梳理、产品营销及整合传播规划的企业团队。</p>
                    <ul className="aim-price-features">
                      <li>专属品牌与产品知识库</li>
                      <li>专属策略流程及模板</li>
                      <li>企业级品牌战略梳理能力</li>
                      <li>可同时 3 人共用账号</li>
                    </ul>
                    <Link className="aim-button aim-button-primary" to={ctaTo}>申请企业版 <span aria-hidden="true">↗</span></Link>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="aim-footer">
        <div className="aim-footer-inner">
          <div className="aim-footer-brand">
            <Link className="aim-wordmark" to="/" aria-label="策略咖首页"><BrandLogo light /></Link>
            <p>KBS品牌营销策略解决方案</p>
          </div>
          <nav className="aim-footer-links">
            <a href="#output">生成成果</a>
            <a href="#workflow">KBS方法</a>
            <a href="#capabilities">策略能力</a>
            <a href="#pricing">方案定价</a>
            <Link to="/security">数据安全</Link>
          </nav>
          <div className="aim-footer-meta">
            <span>© 2026 策略咖</span>
            <span>演示环境，数据均为模拟</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
